import type { Random } from "../core/random.js";
import type { ClubStyle, PhoneMessage, Position, Sender } from "./model.js";
import { AGENT_NAME, PAPER_NAME } from "./names.js";
import { pick } from "./rng.js";

/**
 * El movil de la carrera: lo que te dicen el mister, tu agente, la prensa, la
 * aficion y tu madre.
 *
 * Humor acido a proposito (lo pidio el usuario): el futbol se toma demasiado
 * en serio a si mismo, y el movil es donde se le baja los humos. Nunca contra
 * el jugador por quien es, siempre contra el circo — el mister que se apunta
 * las victorias, el periodico que acierta de chiripa, el agente que cobra
 * hasta por respirar.
 *
 * Cada mensaje guarda su CLAVE y sus parametros (no el texto), para que el
 * movil entero cambie de idioma con el juego. Un parametro que empieza por
 * "§" es a su vez una clave que se traduce al pintarlo (el nombre de una
 * division, un estilo de juego).
 */
export type Draft = Omit<PhoneMessage, "id" | "season" | "matchday">;

const msg = (from: Sender, key: string, params?: Record<string, string | number>): Draft =>
  params ? { from, key, params } : { from, key };

/** Como se describe el estilo de un club en boca de su propio mister. */
export const STYLE_PHRASE: Record<ClubStyle, string> = {
  cerrojo: "con el autobús aparcado",
  presion: "presionando hasta al árbitro",
  toque: "al toque, aunque no sepamos",
  patadon: "al pelotazo, y que Dios reparta suerte",
  contra: "a la contra, esperando el error ajeno",
};

/** Una etiqueta corta por estilo, para la ficha del rival. */
export const STYLE_LABEL: Record<ClubStyle, string> = {
  cerrojo: "Cerrojo",
  presion: "Presión",
  toque: "Toque",
  patadon: "Patadón",
  contra: "Contra",
};

/** Y lo que dice de ellos cualquiera que los haya sufrido. */
export const STYLE_BLURB: Record<ClubStyle, string> = {
  cerrojo: "Aparcan el autobús y le echan el freno de mano.",
  presion: "Presionan hasta al utillero.",
  toque: "Se la pasan como si cobraran por pase.",
  patadon: "El balón pasa más tiempo en el aire que en el suelo.",
  contra: "Te regalan el balón. Es una trampa.",
};

const FAN_HANDLES = ["@ultras_del_sofa", "@peña_la_tribuna", "@tu_vecino_opina", "@abuela_futbolera", "@el_del_bombo"];

export interface Voices {
  name: string;
  club: string;
  coach: string;
}

// ------------------------------------------------------------------ inicio

export function welcomeMessages(v: Voices, style: ClubStyle): Draft[] {
  return [
    msg("agent", "Soy {agent}, tu agente. Me llevo el diez por ciento de todo lo que ganes. De momento es el diez por ciento de nada, así que vamos bien.", {
      agent: AGENT_NAME,
    }),
    msg("coach", "Bienvenido al {club}. Aquí jugamos {estilo}. Tú corre, que para pensar ya estoy yo.", {
      club: v.club,
      estilo: `§${STYLE_PHRASE[style]}`,
    }),
    msg("press", "{paper}: «El {club} ficha a {name}, un chaval de 17 años. Nadie sabe quién es. Él tampoco.»", {
      paper: PAPER_NAME,
      club: v.club,
      name: v.name,
    }),
    msg("mom", "Hijo, dice la vecina que ahora eres futbolista. Abrígate, que en el campo hace fresco."),
  ];
}

// ------------------------------------------------------------ antes del partido
// El informe, el encargo y lo que se contesta: ver `phoneTalk.ts`.

// ------------------------------------------------------------ despues del partido

export interface AfterMatch {
  rival: string;
  result: "win" | "draw" | "loss";
  goalsFor: number;
  goalsAgainst: number;
  goals: number;
  assists: number;
  shots: number;
  mvp: boolean;
  grade: number;
  position: Position;
  absent: boolean;
}

const COACH_AFTER: Record<"win" | "draw" | "loss", readonly string[]> = {
  win: [
    "Bien. No te acostumbres.",
    "Hemos ganado. Mañana entreno a las 8, por si te venías arriba.",
    "Así se juega. Lo diré en la rueda de prensa como si fuera idea mía.",
  ],
  draw: [
    "Un empate. Ni fu ni fa. Como tu pierna mala.",
    "Un punto es un punto. Lo dicen todos los entrenadores que no saben qué decir. Como yo.",
  ],
  loss: [
    "Hoy no. Ni tú, ni yo, ni el utillero.",
    "He visto cosas peores. No recuerdo cuándo.",
    "Mañana doble sesión. La culpa, repartida: un noventa por ciento para vosotros.",
  ],
};

const FANS_AFTER: Record<"win" | "draw" | "loss", readonly string[]> = {
  win: ["¡¡VAMOOOS!! Esto es otra cosa.", "{name} presidente.", "Así sí. Mañana ya pedimos fichajes."],
  draw: ["Empate. Me doy de baja. (No me doy de baja.)", "Pagamos la entrada para esto."],
  loss: [
    "Devolvedme el bocadillo.",
    "Lo que se sufre por unos colores.",
    "Que alguien le explique a {name} dónde está la portería.",
  ],
};

/** El titular del dia: se escoge lo mas llamativo que haya pasado. */
function headline(a: AfterMatch): string {
  const margin = a.goalsFor - a.goalsAgainst;
  if (a.absent) return "{paper}: «El {club} juega sin {name}. Nadie da explicaciones. Su agente, tampoco.»";
  if (a.goals >= 3) return "{paper}: «Hat-trick de {name}. Su madre ya lo ha puesto de fondo de pantalla.»";
  if (a.goals === 2) return "{paper}: «Doblete de {name}. El portero del {rival} se plantea cambiar de oficio.»";
  if (a.grade < 5) return "{paper}: «{name}, un {grade}. Y porque no se puede poner menos.»";
  if (margin <= -3) return "{paper}: «Baño del {rival} al {club}. Hay quien pide la vuelta del entrenador anterior. Y del anterior.»";
  if (margin >= 3) return "{paper}: «Goleada del {club}. El {rival} pide que no cuente.»";
  if (a.goals === 1 && a.result === "win") return "{paper}: «{name} decide ante el {rival}. Se confirman los rumores: sabe jugar.»";
  if (a.goals === 1 && a.result === "loss") return "{paper}: «{name} marca y el {club} pierde. Como achicar agua en el Titanic.»";
  if (a.mvp) return "{paper}: «{name}, el mejor del partido. Él tampoco se lo explica.»";
  if (a.assists >= 2) return "{paper}: «{name} reparte asistencias como un camarero en feria.»";
  if (a.position === "DEL" && a.shots === 0 && a.result === "loss") {
    return "{paper}: «Se busca delantero. Responde al nombre de {name}. Si lo ven, avisen.»";
  }
  if (a.result === "draw") return "{paper}: «Empate entre el {club} y el {rival}. Nadie recordará este partido. Nosotros tampoco.»";
  if (a.result === "win") return "{paper}: «El {club} gana sin brillar. Sin brillar nada, en serio.»";
  return "{paper}: «Derrota del {club}. {coach} habla de \"detalles\". Los detalles fueron los goles.»";
}

export function afterMatchMessages(random: Random, v: Voices, a: AfterMatch): Draft[] {
  const params = {
    paper: PAPER_NAME,
    name: v.name,
    club: v.club,
    coach: v.coach,
    rival: a.rival,
    grade: a.grade.toFixed(1),
  };
  const out: Draft[] = [];
  if (a.absent) {
    out.push(msg("coach", "Te estuvimos esperando. Jugamos sin ti. Nadie notó la diferencia. Es broma. O no."));
  } else if (a.grade >= 8.5) {
    out.push(msg("coach", "Hoy has estado enorme. No se lo digas a nadie, que luego pedís aumento."));
  } else if (a.goalsAgainst - a.goalsFor >= 3) {
    out.push(msg("coach", "Voy a ver el partido otra vez. Por si fue una pesadilla. Spoiler: no."));
  } else {
    out.push(msg("coach", pick(random, COACH_AFTER[a.result])));
  }
  out.push(msg("press", headline(a), params));
  if (!a.absent) {
    const line = a.mvp && random() < 0.5 ? "{name} es lo único bueno de este club. Y del barrio." : pick(random, FANS_AFTER[a.result]);
    out.push(msg("fans", line, { name: v.name, who: pick(random, FAN_HANDLES) }));
  }
  return out;
}

/** Tres seguidas, para bien o para mal, merecen portada. */
export function streakMessage(v: Voices, lastResults: ReadonlyArray<"win" | "draw" | "loss">): Draft | null {
  const last3 = lastResults.slice(-3);
  if (last3.length < 3) return null;
  // Solo al llegar a tres justas: con cuatro seguidas no se repite la portada.
  const prev = lastResults.at(-4);
  if (last3.every((r) => r === "win") && prev !== "win") {
    return msg("press", "{paper}: «El {club} encadena tres victorias y ya se habla de proyecto. De qué proyecto, no se sabe.»", {
      paper: PAPER_NAME,
      club: v.club,
    });
  }
  if (last3.every((r) => r === "loss") && prev !== "loss") {
    return msg("press", "{paper}: «Tres derrotas seguidas. El presidente da su apoyo total a {coach}: está sentenciado.»", {
      paper: PAPER_NAME,
      coach: v.coach,
    });
  }
  return null;
}

// ------------------------------------------------------------ fin de temporada

export interface SeasonEndInput {
  season: number;
  position: number;
  division: string;
  nextDivision: string;
  champion: boolean;
  promoted: boolean;
  relegated: boolean;
  bottomOfLadder: boolean;
  topTier: boolean;
  goals: number;
  grade: number;
  age: number;
}

export function seasonEndMessages(v: Voices, s: SeasonEndInput): Draft[] {
  const params = {
    paper: PAPER_NAME,
    club: v.club,
    name: v.name,
    division: `§${s.division}`,
    nextDivision: `§${s.nextDivision}`,
    pos: s.position,
  };
  const out: Draft[] = [];
  if (s.champion && s.topTier) {
    out.push(msg("press", "{paper}: «{name} levanta la Primera con el {club}. Ya puede morir tranquilo. Su agente, más.»", params));
  } else if (s.champion) {
    out.push(msg("press", "{paper}: «¡El {club}, campeón de {division}! La afición lo celebra en la rotonda de siempre.»", params));
  } else if (s.promoted) {
    out.push(msg("press", "{paper}: «El {club} sube a {nextDivision}. Nadie sabe muy bien cómo, pero sube.»", params));
  } else if (s.relegated) {
    out.push(msg("press", "{paper}: «El {club} baja a {nextDivision}. El presidente anuncia un proyecto ilusionante.»", params));
  } else if (s.bottomOfLadder) {
    out.push(msg("press", "{paper}: «El {club} acaba último en el Barrio. Por suerte, más abajo no hay nada.»", params));
  } else {
    out.push(msg("press", "{paper}: «Temporada tranquila del {club}: {pos}.º. Ni frío ni calor. Tibia, como la cerveza del bar.»", params));
  }
  out.push(
    msg("coach", "Temporada {season}: {goals} goles y un {grade} de media. Podría ser peor. Podrías ser el portero.", {
      season: s.season,
      goals: s.goals,
      grade: s.grade.toFixed(1),
    }),
  );
  out.push(birthdayMessage(s.age));
  if (s.age === 31) {
    out.push(
      msg("press", "{paper}: «{name} cumple {age}: ya le llaman \"veterano\" con cariño, y \"abuelo\" sin él.»", {
        paper: PAPER_NAME,
        name: v.name,
        age: s.age,
      }),
    );
  }
  return out;
}

function birthdayMessage(age: number): Draft {
  if (age === 21) return msg("mom", "Felicidades. Veintiuno. Tu primo ya tiene plaza fija. Solo lo digo.");
  if (age === 30) return msg("mom", "Treinta, hijo. Tu primo ya tiene hipoteca. ¿Y tú qué tienes? Ah, sí: tendinitis.");
  if (age >= 33) return msg("mom", "Hijo, que ya tienes una edad. ¿Y si te vienes a la tienda? Buscan reponedor.");
  return msg("mom", "Felicidades, hijo. {age} años. ¿Has pensado en estudiar algo, por si acaso?", { age });
}

// ------------------------------------------------------------ ofertas

export const OFFER_PITCHES: readonly string[] = [
  "Pagan puntual. Casi siempre.",
  "Tienen un proyecto. No saben cuál, pero tienen uno.",
  "El presidente quiere tu perfil. El de Instagram, creo.",
  "Dicen que serás importante. Se lo dicen a todos.",
  "El estadio tiene techo. En una grada.",
  "Tienen fisio. Lo comparten con el equipo de balonmano.",
];

export function offerMessages(offers: ReadonlyArray<{ club: string; topTier: boolean }>, age: number): Draft[] {
  if (offers.length === 0) {
    return [msg("agent", "No ha llamado nadie. He comprobado que el móvil tenía batería. Tenía.")];
  }
  const out: Draft[] = [];
  const top = offers.find((o) => o.topTier);
  if (top) {
    out.push(msg("agent", "¡Primera! Te quiere el {club}. Me voy a comprar un barco. Pequeño, de momento.", { club: top.club }));
  } else if (offers.length === 1) {
    out.push(msg("agent", "Ha llamado el {club}. Dicen que te han visto jugar. No pregunté cuándo.", { club: offers[0]!.club }));
  } else {
    out.push(msg("agent", "Tengo {n} ofertas encima de la mesa. Bueno, en el móvil. Elige, que yo cobro igual.", { n: offers.length }));
  }
  if (age >= 32) {
    out.push(msg("agent", "A tu edad, cada oferta es un regalo. Pocas, y huelen a naftalina."));
  }
  return out;
}

export function signedMessages(v: Voices, style: ClubStyle, oldCoach: string): Draft[] {
  return [
    msg("coach", "Bienvenido al {club}. Aquí jugamos {estilo}. Olvida lo que te enseñó {old}. Sobre todo eso.", {
      club: v.club,
      estilo: `§${STYLE_PHRASE[style]}`,
      old: oldCoach,
    }),
    msg("press", "{paper}: «{name} ficha por el {club}. {agent} se frota las manos.»", {
      paper: PAPER_NAME,
      name: v.name,
      club: v.club,
      agent: AGENT_NAME,
    }),
  ];
}

export function stayedMessages(): Draft[] {
  return [msg("coach", "Te quedas. Me alegro. No se lo digas a nadie, pero me alegro.")];
}

// ------------------------------------------------------------ camisetas

export function kitMessage(
  reason: "season" | "swap" | "reward",
  params: { club?: string; star?: string; rival?: string; kit?: string; how?: string },
): Draft {
  if (reason === "season") {
    return msg("system", "Te quedas la camiseta del {club}. Ya puedes vestirla online.", { club: params.club ?? "" });
  }
  if (reason === "swap") {
    return msg("system", "Te has cambiado la camiseta con {star}, del {rival}. Olía a linimento y a derrota. Ya puedes vestirla online.", {
      star: params.star ?? "",
      rival: params.rival ?? "",
    });
  }
  return msg("system", "Equipación nueva: {kit}. {how}", { kit: `§${params.kit ?? ""}`, how: `§${params.how ?? ""}` });
}

// ------------------------------------------------------------ retirada

export function retirementMessages(v: Voices, seasons: number): Draft[] {
  return [
    msg("press", "{paper}: «{name} cuelga las botas tras {seasons} temporadas. Las botas, aliviadas.»", {
      paper: PAPER_NAME,
      name: v.name,
      seasons,
    }),
    msg("agent", "Ha sido un placer. El diez por ciento de un placer, concretamente."),
    msg("mom", "Hijo, ya era hora. Tu habitación sigue igual. Tus trofeos, también: sin polvo."),
  ];
}

/** El epitafio del salon de la fama, segun hasta donde llegaste. */
export function hallOfFameVerdict(peakTier: number, primeraTitles: number): string {
  if (primeraTitles > 0) return "Leyenda. Tendrá una estatua. Fea, pero una estatua.";
  if (peakTier >= 3) return "Llegó a Primera. En su pueblo le han puesto una calle. Es un callejón.";
  if (peakTier === 2) return "Un clásico de Segunda. Los árbitros le saludaban por su nombre.";
  if (peakTier === 1) return "Leyenda del Regional. Invicto en las barbacoas del club.";
  return "Nunca salió del barrio. El barrio tampoco lo echará de menos.";
}
